// src/pages/ReportsOverviewPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Select,
  Spinner,
  Table,
} from "flowbite-react";
import { useQuery } from "react-query";
import axios from "../config/axiosInstance";
import { CRM_REPORTS, USERS, CRM } from "../config/apiConfig";
import {
  HiOutlineTrendingUp,
  HiOutlineUserGroup,
  HiOutlineChartPie,
  HiOutlineCalendar,
  HiOutlineRefresh,
} from "react-icons/hi";

// ApexCharts
import Chart from "react-apexcharts";
import moment from "moment";

// ============================
// Helpers de formatação
// ============================
const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "R$ 0,00";

  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0,0%";
  return `${num.toFixed(1)}%`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ============================
// Helpers de normalização
// ============================
const normalizeListResponse = (payload, label = "items") => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;

  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `[ReportsOverviewPage] Resposta inesperada para ${label}:`,
      payload
    );
  }

  return [];
};

const normalizeReport = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {
      summary: {},
      deals: {},
      leads: {},
      activities: {},
      timeAnalytics: {},
      filters: {},
    };
  }

  const {
    summary = {},
    deals = {},
    leads = {},
    activities = {},
    timeAnalytics = {},
    filters = {},
  } = raw;

  return { summary, deals, leads, activities, timeAnalytics, filters };
};

const CHART_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f97316",
  "#a855f7",
  "#ef4444",
  "#06b6d4",
  "#facc15",
];

const LEAD_STATUS_LABELS = {
  new: "Novo",
  working: "Em andamento",
  qualified: "Qualificado",
  unqualified: "Descartado",
  won: "Ganho",
  lost: "Perdido",
};

const DEAL_STATUS_LABELS = {
  won: "Ganho",
  lost: "Perdido",
  open: "Aberto",
  working: "Em andamento",
  qualified: "Qualificado",
  unqualified: "Descartado",
};

const translateLeadStatus = (status) =>
  LEAD_STATUS_LABELS[status] || (status ? status : "—");

const translateDealStatus = (status) =>
  DEAL_STATUS_LABELS[status] || (status ? status : "—");

const LEAD_SOURCE_LABELS = {
  manual: "Manual",
  website: "Site",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  google_ads: "Google Ads",
  organic: "Orgânico",
  referral: "Indicação",
  event: "Evento",
  import: "Importado",
  "lead-generator": "Gerador de leads",
};

const translateLeadSource = (source) =>
  LEAD_SOURCE_LABELS[source] || (source ? source : "Outros");

// ============================
// Componente principal
// ============================
const ReportsOverviewPage = () => {
  const [range, setRange] = useState("30d");
  const [ownerId, setOwnerId] = useState("");
  const [funnelId, setFunnelId] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const FUNNEL_STORAGE_KEY = "reports-last-funnel-id";

  // ============================
  // Fetch de usuários (owners)
  // ============================
  const { data: usersResp } = useQuery(
    ["users-all"],
    async () => {
      const res = await axios.get(USERS.GET_USERS);
      return res.data?.data || res.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const owners = useMemo(
    () => normalizeListResponse(usersResp, "users"),
    [usersResp]
  );

  // ============================
  // Fetch de funis
  // ============================
  const { data: funnelsResp } = useQuery(
    ["crm-funnels"],
    async () => {
      const res = await axios.get(CRM.FUNNELS);
      return res.data?.data || res.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const funnels = useMemo(
    () => normalizeListResponse(funnelsResp, "funnels"),
    [funnelsResp]
  );

  useEffect(() => {
    if (!funnels || funnels.length === 0) return;
    const rawSaved = localStorage.getItem(FUNNEL_STORAGE_KEY);
    const isInvalid =
      !rawSaved ||
      rawSaved === "undefined" ||
      rawSaved === "null" ||
      rawSaved === "NaN";
    const normalizedSaved = isInvalid ? null : rawSaved;
    if (isInvalid) {
      localStorage.removeItem(FUNNEL_STORAGE_KEY);
    }
    const preferred =
      normalizedSaved &&
      funnels.find((f) => String(f.id) === normalizedSaved)?.id;
    const fallback = preferred ?? funnels[0]?.id;
    const target = fallback != null ? String(fallback) : null;
    if (target && target !== funnelId) {
      setFunnelId(target);
      localStorage.setItem(FUNNEL_STORAGE_KEY, target);
    }
  }, [funnels, funnelId]);

  // ============================
  // Fetch principal: /reports/overview
  // ============================
  const {
    data: reportResp,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(
    [
      "crm-reports-overview",
      { range, ownerId, funnelId, customStart, customEnd },
    ],
    async () => {
      const params = { range };

      if (ownerId) params.ownerId = Number(ownerId);
      if (funnelId) params.funnelId = funnelId;

      if (range === "custom") {
        if (customStart) params.startDate = customStart;
        if (customEnd) params.endDate = customEnd;
      }

      const res = await axios.get(CRM_REPORTS.GET_OVERVIEW, { params });
      return res.data?.data || res.data;
    },
    { keepPreviousData: true }
  );

  const { summary, deals, leads, activities, timeAnalytics, filters } = useMemo(
    () => normalizeReport(reportResp),
    [reportResp]
  );

  const isBusy = isLoading || isFetching;

  // ============================
  // Dados tratados para gráficos
  // ============================
  // Linha: Leads x Deals por semana
  const weeklyTimeSeries = useMemo(() => {
    const map = new Map();

    (timeAnalytics.dealsByWeek || []).forEach((row) => {
      const key = row.week;
      const current = map.get(key) || { week: key };
      current.dealsCreated = row.created || 0;
      current.dealsWon = row.won || 0;
      current.dealsLost = row.lost || 0;
      map.set(key, current);
    });

    (timeAnalytics.leadsByWeek || []).forEach((row) => {
      const key = row.week;
      const current = map.get(key) || { week: key };
      current.leadsTotal = row.total || 0;
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.week).localeCompare(String(b.week))
    );
  }, [timeAnalytics]);

  const weeklyChartOptions = useMemo(
    () => ({
      chart: {
        id: "weekly-line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: weeklyTimeSeries.map((r) => r.week),
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: { style: { fontSize: "11px" } },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
        markers: { radius: 12 },
      },
      grid: {
        borderColor: "#e5e7eb",
      },
      colors: CHART_COLORS,
      tooltip: { theme: "dark" },
    }),
    [weeklyTimeSeries]
  );

  const weeklyChartSeries = useMemo(
    () => [
      {
        name: "Leads",
        data: weeklyTimeSeries.map((r) => r.leadsTotal || 0),
      },
      {
        name: "Deals criados",
        data: weeklyTimeSeries.map((r) => r.dealsCreated || 0),
      },
      {
        name: "Deals ganhos",
        data: weeklyTimeSeries.map((r) => r.dealsWon || 0),
      },
      {
        name: "Deals perdidos",
        data: weeklyTimeSeries.map((r) => r.dealsLost || 0),
      },
    ],
    [weeklyTimeSeries]
  );

  // Donut: distribuição por status
  const dealsByStatusPieData = useMemo(
    () =>
      (deals.byStatus || []).map((row) => ({
        name: translateDealStatus(row.status),
        value: row.count || 0,
      })),
    [deals]
  );

  const dealsStatusOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels: dealsByStatusPieData.map((r) => r.name),
      legend: {
        position: "bottom",
        fontSize: "11px",
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} negócios`,
        },
      },
      colors: CHART_COLORS,
    }),
    [dealsByStatusPieData]
  );

  const dealsStatusSeries = useMemo(
    () => dealsByStatusPieData.map((r) => r.value),
    [dealsByStatusPieData]
  );

  // Barras: negócios por etapa do funil
  const dealsByStageChartData = useMemo(
    () =>
      (deals.byStage || []).map((row) => ({
        stageName: row.stageName || "—",
        count: row.count || 0,
        value: row.value || 0,
      })),
    [deals]
  );

  const dealsStageOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "45%",
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: dealsByStageChartData.map((r) => r.stageName),
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: { style: { fontSize: "11px" } },
      },
      legend: { position: "top", horizontalAlign: "left" },
      grid: { borderColor: "#e5e7eb" },
      colors: [CHART_COLORS[0], CHART_COLORS[5]],
      tooltip: {
        y: [
          {
            formatter: (val) => `${val} negócios`,
          },
          {
            formatter: (val) => formatCurrency(val),
          },
        ],
      },
    }),
    [dealsByStageChartData]
  );

  const dealsStageSeries = useMemo(
    () => [
      {
        name: "Qtde de negócios",
        data: dealsByStageChartData.map((r) => r.count),
      },
      {
        name: "Valor total",
        data: dealsByStageChartData.map((r) => r.value),
      },
    ],
    [dealsByStageChartData]
  );

  // Barras horizontais: desempenho por responsável
  const dealsByOwnerChartData = useMemo(() => {
    const map = new Map();

    (deals.byOwner || []).forEach((row) => {
      const key = row.ownerId || row.ownerName || "—";
      const current = map.get(key) || {
        owner: row.ownerName || "—",
        dealsCount: 0,
        dealsValue: 0,
        activitiesDone: 0,
      };
      current.dealsCount += row.count || 0;
      current.dealsValue += row.value || 0;
      map.set(key, current);
    });

    (activities.byOwner || []).forEach((row) => {
      const key = row.ownerId || row.ownerName || "—";
      const current = map.get(key) || {
        owner: row.ownerName || "—",
        dealsCount: 0,
        dealsValue: 0,
        activitiesDone: 0,
      };
      current.activitiesDone += row.done || 0;
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.owner.localeCompare(b.owner)
    );
  }, [deals, activities]);

  const dealsOwnerOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "60%",
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: dealsByOwnerChartData.map((r) => r.owner),
        labels: { style: { fontSize: "11px" } },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      grid: { borderColor: "#e5e7eb" },
      colors: [CHART_COLORS[0], CHART_COLORS[1]],
      tooltip: {
        y: [
          {
            formatter: (val) => `${val} negócios`,
          },
          {
            formatter: (val) => `${val} atividades`,
          },
        ],
      },
    }),
    [dealsByOwnerChartData]
  );

  const dealsOwnerSeries = useMemo(
    () => [
      {
        name: "Negócios",
        data: dealsByOwnerChartData.map((r) => r.dealsCount),
      },
      {
        name: "Atividades feitas",
        data: dealsByOwnerChartData.map((r) => r.activitiesDone),
      },
    ],
    [dealsByOwnerChartData]
  );

  const dealsWeeklyCategories = useMemo(
    () => (timeAnalytics.dealsByWeek || []).map((row) => row.week),
    [timeAnalytics.dealsByWeek]
  );

  const dealsWeeklySeries = useMemo(
    () => [
      {
        name: "Criados",
        data: (timeAnalytics.dealsByWeek || []).map((row) => row.created || 0),
      },
      {
        name: "Ganhos",
        data: (timeAnalytics.dealsByWeek || []).map((row) => row.won || 0),
      },
    ],
    [timeAnalytics.dealsByWeek]
  );

  const dealsWeeklyOptions = useMemo(
    () => ({
      chart: {
        id: "deals-weekly-line",
        toolbar: { show: false },
        zoom: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 2 },
      tooltip: { theme: "dark" },
      colors: [CHART_COLORS[0], CHART_COLORS[2]],
      grid: { strokeDashArray: 4 },
      xaxis: { categories: dealsWeeklyCategories, labels: { show: false } },
      legend: { show: false },
    }),
    [dealsWeeklyCategories]
  );

  const leadsWeeklySeries = useMemo(
    () => [
      {
        name: "Leads ativos",
        data: (timeAnalytics.leadsByWeek || []).map((row) => row.total || 0),
      },
    ],
    [timeAnalytics.leadsByWeek]
  );

  const leadsWeeklyOptions = useMemo(
    () => ({
      chart: {
        id: "leads-weekly-line",
        toolbar: { show: false },
        zoom: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 2 },
      tooltip: { theme: "dark" },
      colors: [CHART_COLORS[5]],
      grid: { strokeDashArray: 4 },
      xaxis: {
        categories: (timeAnalytics.leadsByWeek || []).map((row) => row.week),
        labels: { show: false },
      },
      legend: { show: false },
    }),
    [timeAnalytics.leadsByWeek]
  );

  const weekdayLabels = useMemo(
    () =>
      (timeAnalytics.dealsByWeekday || []).map(
        (row) => row.label || row.weekday
      ),
    [timeAnalytics.dealsByWeekday]
  );

  const weekdaySeries = useMemo(
    () => [
      {
        name: "Negócios",
        data: (timeAnalytics.dealsByWeekday || []).map((row) => row.count || 0),
      },
    ],
    [timeAnalytics.dealsByWeekday]
  );

  const weekdayOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: { columnWidth: "60%", borderRadius: 4 },
      },
      colors: [CHART_COLORS[1]],
      xaxis: {
        categories: weekdayLabels,
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      legend: { show: false },
      grid: { borderColor: "#e5e7eb" },
    }),
    [weekdayLabels]
  );

  const hourLabels = useMemo(
    () =>
      (timeAnalytics.dealsByHour || []).map(
        (row) => `${String(row.hour).padStart(2, "0")}:00`
      ),
    [timeAnalytics.dealsByHour]
  );

  const hourSeries = useMemo(
    () => [
      {
        name: "Negócios",
        data: (timeAnalytics.dealsByHour || []).map((row) => row.count || 0),
      },
    ],
    [timeAnalytics.dealsByHour]
  );

  const hourOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { columnWidth: "70%", borderRadius: 4 } },
      colors: [CHART_COLORS[4]],
      xaxis: {
        categories: hourLabels,
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      legend: { show: false },
      grid: { borderColor: "#e5e7eb" },
    }),
    [hourLabels]
  );

  const leadsByStatusPieData = useMemo(
    () =>
      (leads.byStatus || []).map((row) => ({
        name: translateLeadStatus(row.status),
        value: row.count || 0,
      })),
    [leads]
  );

  const leadsStatusOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels: leadsByStatusPieData.map((r) => r.name),
      legend: {
        position: "bottom",
        fontSize: "11px",
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} leads`,
        },
      },
      colors: CHART_COLORS,
    }),
    [leadsByStatusPieData]
  );

  const leadsStatusSeries = useMemo(
    () => leadsByStatusPieData.map((r) => r.value),
    [leadsByStatusPieData]
  );

  const topLeadSources = useMemo(
    () =>
      (leads.bySource || [])
        .slice()
        .sort((a, b) => (b.count || 0) - (a.count || 0))
        .slice(0, 3),
    [leads]
  );

  const stageHighlights = useMemo(
    () => dealsByStageChartData.slice(0, 3),
    [dealsByStageChartData]
  );

  // ============================
  // Componentes auxiliares
  // ============================
  const RangeChip = ({ label, value }) => (
    <button
      type="button"
      onClick={() => setRange(value)}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
        range === value
          ? "border-sky-500 bg-sky-50 text-sky-700"
          : "border-gray-200 bg-white text-gray-600 hover:border-sky-400 hover:text-sky-700"
      }`}
    >
      {label}
    </button>
  );

  const onChangeCustomStart = (e) => {
    setCustomStart(e.target.value);
    setRange("custom");
  };

  const onChangeCustomEnd = (e) => {
    setCustomEnd(e.target.value);
    setRange("custom");
  };

  const selectedOwner =
    owners?.find((u) => String(u.id) === String(ownerId)) || null;

  const selectedFunnel =
    funnels?.find((f) => String(f.id) === String(funnelId)) || null;

  // ============================
  // Render
  // ============================
  return (
    <div className="px-2 py-2 space-y-5">
      {/* Filtros */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        {/* faixa superior */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gray-400" />

        <div className="relative p-4 md:p-5 space-y-4">
          {/* Cabeçalho + resumo dos filtros */}
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                Filtros do relatório
              </p>
              <p className="text-sm text-slate-500">
                Ajuste período, responsável e funil para analisar os resultados
                da sua operação.
              </p>
            </div>

            {/* Chips de filtros ativos */}
            <div className="flex flex-wrap gap-2 text-[11px]">
              {filters?.start && filters?.end && (
                <span className="inline-flex items-center gap-1 rounded-full border border-sky-100 bg-sky-50 px-2 py-1 text-sky-700">
                  <HiOutlineCalendar className="h-3 w-3" />
                  <span>
                    {moment(filters.start).format("DD/MM/yyyy")} –{" "}
                    {moment(filters.end).format("DD/MM/yyyy")}
                  </span>
                </span>
              )}

              {selectedOwner && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-1 text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span>
                    Resp.: {selectedOwner.name || selectedOwner.email}
                  </span>
                </span>
              )}

              {selectedFunnel && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-100 bg-violet-50 px-2 py-1 text-violet-700">
                  <span className="h-2 w-2 rounded-full bg-violet-500" />
                  <span>Funil: {selectedFunnel.name}</span>
                </span>
              )}

              {!filters?.start &&
                !filters?.end &&
                !selectedOwner &&
                !selectedFunnel && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-100 bg-slate-50 px-2 py-1 text-slate-500">
                    <span className="h-2 w-2 rounded-full bg-slate-300" />
                    <span>Nenhum filtro específico aplicado</span>
                  </span>
                )}
            </div>
          </div>

          {/* Linha divisória suave */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          {/* Grid de campos */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {/* Range rápido */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <HiOutlineCalendar className="h-4 w-4 text-sky-500" />
                  Período rápido
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <RangeChip label="Últimos 7 dias" value="7d" />
                <RangeChip label="Últimos 30 dias" value="30d" />
                <RangeChip label="Últimos 90 dias" value="90d" />
                <RangeChip label="Este mês" value="month" />
                <RangeChip label="Este ano" value="year" />
              </div>

              {filters?.start && filters?.end && (
                <p className="mt-1 text-[11px] text-gray-400">
                  Aplicado:{" "}
                  <span className="font-medium text-gray-600">
                    {moment(filters.start).format("DD/MM/yyyy")} até{" "}
                    {moment(filters.end).format("DD/MM/yyyy")}
                  </span>
                </p>
              )}
            </div>

            {/* Range customizado */}
            <div className="space-y-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Período personalizado
              </span>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  type="date"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:border-sky-500 focus:ring-sky-500"
                  value={customStart}
                  onChange={onChangeCustomStart}
                />
                <span className="text-xs text-gray-400 text-center">até</span>
                <input
                  type="date"
                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs focus:border-sky-500 focus:ring-sky-500"
                  value={customEnd}
                  onChange={onChangeCustomEnd}
                />
              </div>
              {range === "custom" && (customStart || customEnd) && (
                <div className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-1">
                  <span className="h-2 w-2 rounded-full bg-sky-500" />
                  <span className="text-[11px] font-medium text-sky-700">
                    Filtro de data customizado ativo
                  </span>
                </div>
              )}
            </div>

            {/* Responsável */}
            <div className="space-y-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Responsável
              </span>
              <Select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
                sizing="sm"
              >
                <option value="">Todos</option>
                {(owners || []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </Select>
              {selectedOwner && (
                <p className="text-[11px] text-gray-400">
                  Exibindo resultados atribuídos a{" "}
                  <span className="font-medium text-gray-700">
                    {selectedOwner.name || selectedOwner.email}
                  </span>
                  .
                </p>
              )}
            </div>

            {/* Funil */}
            <div className="space-y-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Funil
              </span>
              <Select
                value={funnelId ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFunnelId(value);
                  if (value) {
                    localStorage.setItem(FUNNEL_STORAGE_KEY, value);
                  }
                }}
                sizing="sm"
                disabled={!funnels?.length}
              >
                {(funnels || []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
              {selectedFunnel && (
                <p className="text-[11px] text-gray-400">
                  Filtrando etapas do funil{" "}
                  <span className="font-medium text-gray-700">
                    {selectedFunnel.name}
                  </span>
                  .
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Alert color="failure">
          <span className="font-medium">Erro ao carregar relatórios.</span>{" "}
          Tente novamente em instantes.
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500">
              Calculando os relatórios da sua operação...
            </p>
          </div>
        </div>
      )}

      {/* Nenhum dado (mas sem erro) */}
      {!isLoading && !error && !reportResp && (
        <Alert color="info">
          Ainda não há dados para o período selecionado. Ajuste os filtros ou
          aguarde novas movimentações no CRM.
        </Alert>
      )}

      {/* Conteúdo principal */}
      {!isLoading && !error && reportResp && (
        <>
          {/* KPIs principais */}
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
            {/* Leads criados */}
            <div className="relative overflow-hidden border border-slate-200/80 rounded-lg p-4 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Leads criados
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {summary.totalLeads || 0}
                  </p>
                </div>
                <div className="rounded-full bg-sky-50 p-2">
                  <HiOutlineUserGroup className="h-5 w-5 text-sky-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Total de leads dentro do período selecionado.
              </p>
            </div>

            {/* Leads convertidos */}
            <div className="relative overflow-hidden border border-slate-200/80 rounded-lg p-4 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Leads convertidos
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {summary.convertedLeads || 0}
                  </p>
                  <p className="mt-1 text-xs text-emerald-700">
                    Taxa: {formatPercent(summary.leadConversionRate || 0)}
                  </p>
                </div>
                <div className="rounded-full bg-emerald-50 p-2">
                  <HiOutlineTrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Leads marcados como &quot;convertidos&quot;.
              </p>
            </div>

            {/* Negócios ganhos */}
            <div className="relative overflow-hidden border border-slate-200/80 rounded-lg p-4 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-indigo-400 to-indigo-500" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Negócios ganhos
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {summary.wonDeals || 0}
                  </p>
                  <p className="mt-1 text-xs text-indigo-700">
                    {formatCurrency(summary.totalWonValue || 0)}
                  </p>
                </div>
                <div className="rounded-full bg-indigo-50 p-2">
                  <HiOutlineChartPie className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Cards com status &quot;ganho&quot;.
              </p>
            </div>

            {/* Negócios perdidos */}
            <div className="relative overflow-hidden border border-slate-200/80 rounded-lg p-4 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-red-500" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Negócios perdidos
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {summary.lostDeals || 0}
                  </p>
                  <p className="mt-1 text-xs text-rose-700">
                    {formatCurrency(summary.totalLostValue || 0)}
                  </p>
                </div>
                <div className="rounded-full bg-rose-50 p-2">
                  <HiOutlineChartPie className="h-5 w-5 text-rose-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Cards com status &quot;perdido&quot;.
              </p>
            </div>

            {/* Ticket médio */}
            <div className="relative overflow-hidden border border-slate-200/80 rounded-lg p-4 bg-white shadow-sm">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Ticket médio
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {formatCurrency(summary.avgTicket || 0)}
                  </p>
                </div>
                <div className="rounded-full bg-amber-50 p-2">
                  <HiOutlineTrendingUp className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Valor médio dos negócios ganhos.
              </p>
            </div>
          </div>

          {/* ============================
              BLOCO DE GRÁFICOS (ApexCharts)
             ============================ */}
          <div className="grid gap-4 xl:grid-cols-2">
            {/* Linha: Leads x Deals por semana */}
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Evolução semanal de Leads &amp; Deals
                </h2>
                <Badge color="indigo" size="xs">
                  Timeline
                </Badge>
              </div>
              <div className="mt-4 h-64 w-full">
                {weeklyTimeSeries.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    Sem dados suficientes para montar o gráfico.
                  </div>
                ) : (
                  <Chart
                    options={weeklyChartOptions}
                    series={weeklyChartSeries}
                    type="line"
                    height="100%"
                    width="100%"
                  />
                )}
              </div>
            </Card>

            {/* Donut: distribuição por status */}
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Distribuição de negócios por status
                </h2>
                <Badge color="purple" size="xs">
                  Funil
                </Badge>
              </div>
              <div className="mt-4 flex flex-col items-center gap-4 md:flex-row">
                <div className="h-56 w-full md:w-1/2">
                  {dealsStatusSeries.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                      Sem dados de negócios para o filtro atual.
                    </div>
                  ) : (
                    <Chart
                      options={dealsStatusOptions}
                      series={dealsStatusSeries}
                      type="donut"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  {dealsByStatusPieData.map((row, index) => (
                    <div
                      key={row.name}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-3 w-3 rounded-sm"
                          style={{
                            backgroundColor:
                              CHART_COLORS[index % CHART_COLORS.length],
                          }}
                        />
                        <span className="text-gray-700">{row.name}</span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {row.value}
                      </span>
                    </div>
                  ))}
                  {dealsByStatusPieData.length === 0 && (
                    <p className="text-xs text-gray-400">
                      Conforme as oportunidades forem avançando no funil, a
                      distribuição aparecerá aqui.
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Gráfico: Negócios por etapa + Desempenho por responsável */}
          <div className="grid gap-4 xl:grid-cols-2">
            {/* Barras: por etapa do funil */}
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Negócios por etapa do funil
                </h2>
                <Badge color="cyan" size="xs">
                  Etapas
                </Badge>
              </div>
              <div className="mt-4 h-64 w-full">
                {dealsByStageChartData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    Sem dados de etapas para o filtro atual.
                  </div>
                ) : (
                  <Chart
                    options={dealsStageOptions}
                    series={dealsStageSeries}
                    type="bar"
                    height="100%"
                    width="100%"
                  />
                )}
              </div>
            </Card>

            {/* Barras horizontais: por responsável */}
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Desempenho por responsável
                </h2>
                <Badge color="emerald" size="xs">
                  Equipe
                </Badge>
              </div>
              <div className="mt-4 h-64 w-full">
                {dealsByOwnerChartData.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    Sem dados por responsável para o filtro atual.
                  </div>
                ) : (
                  <Chart
                    options={dealsOwnerOptions}
                    series={dealsOwnerSeries}
                    type="bar"
                    height="100%"
                    width="100%"
                  />
                )}
              </div>
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Oportunidades em análise
                </h2>
                <Badge color="info" size="xs">
                  Leads
                </Badge>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Distribuição por status e principais origens monitoradas no
                período selecionado.
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-[1fr,120px]">
                <div className="h-48 w-full">
                  {leadsStatusSeries.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                      Sem dados suficientes para montar o gráfico.
                    </div>
                  ) : (
                    <Chart
                      options={leadsStatusOptions}
                      series={leadsStatusSeries}
                      type="donut"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
                <div className="space-y-3">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                    <p className="text-[11px] text-gray-500">Conversão média</p>
                    <p className="mt-1 text-lg font-semibold text-gray-900">
                      {formatPercent(summary.leadConversionRate || 0)}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      Ticket médio de {formatCurrency(summary.avgTicket || 0)}.
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-medium text-gray-500">
                      Top origens
                    </p>
                    {(topLeadSources || []).map((source) => (
                      <div
                        key={source.source}
                        className="flex items-center justify-between text-[12px]"
                      >
                        <span className="text-gray-600">
                          {translateLeadSource(source.source)}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {source.count}
                        </span>
                      </div>
                    ))}
                    {(topLeadSources || []).length === 0 && (
                      <p className="text-[11px] text-gray-400">
                        Ajuste os filtros para visualizar suas origens.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-gray-500">
                  Idade dos leads
                </p>
                <div className="flex flex-wrap gap-2">
                  {(leads.agingBuckets || []).length === 0 ? (
                    <p className="text-xs text-gray-400">
                      Sem dados de aging para o período atual.
                    </p>
                  ) : (
                    leads.agingBuckets.map((bucket) => (
                      <div
                        key={bucket.label}
                        className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs min-w-[120px]"
                      >
                        <span className="font-medium text-gray-600">
                          {bucket.label}
                        </span>
                        <span className="font-semibold text-gray-900 ml-2">
                          ({bucket.count})
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Card>
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Negócios no pipeline
                </h2>
                <Badge color="purple" size="xs">
                  Deals
                </Badge>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Visualize contagens e valores por etapa para interpretar o
                progresso.
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-[1fr,150px]">
                <div className="h-56 w-full">
                  {dealsStageSeries[0].data.every((value) => value === 0) &&
                  dealsStageSeries[1].data.every((value) => value === 0) ? (
                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                      Nenhum estágio registrado com dados no filtro atual.
                    </div>
                  ) : (
                    <Chart
                      options={dealsStageOptions}
                      series={dealsStageSeries}
                      type="bar"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
                <div className="space-y-2 text-xs">
                  {stageHighlights.length === 0 && (
                    <p className="text-gray-400">
                      Ajuste filtros para acompanhar etapas ativas.
                    </p>
                  )}
                  {stageHighlights.map((stage) => (
                    <div
                      key={stage.stageName}
                      className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          {stage.stageName}
                        </span>
                        <span className="text-xs font-semibold text-gray-900">
                          {stage.count} negócios
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        {formatCurrency(stage.value || 0)} em valor
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Atividades */}
          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Atividades por tipo e responsável
                </h2>
                <Badge color="gray" size="xs">
                  Atividades
                </Badge>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {/* Atividades por tipo */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">
                    Atividades por tipo
                  </p>
                  <div className="max-h-60 overflow-auto rounded-md border border-slate-100">
                    <Table hoverable={true} size="xs">
                      <Table.Head>
                        <Table.HeadCell>Tipo</Table.HeadCell>
                        <Table.HeadCell className="text-right">
                          Qtde
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {(activities.byType || []).map((row) => (
                          <Table.Row key={row.type}>
                            <Table.Cell className="text-xs">
                              {row.type || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-right text-xs">
                              {row.count}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        {(activities.byType || []).length === 0 && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={2}
                              className="py-3 text-center text-xs text-gray-400"
                            >
                              Sem atividades no período.
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>

                {/* Atividades por owner */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">
                    Atividades por responsável
                  </p>
                  <div className="max-h-60 overflow-auto rounded-md border border-slate-100">
                    <Table hoverable={true} size="xs">
                      <Table.Head>
                        <Table.HeadCell>Responsável</Table.HeadCell>
                        <Table.HeadCell className="text-right">
                          Feitas
                        </Table.HeadCell>
                        <Table.HeadCell className="text-right">
                          Pendentes
                        </Table.HeadCell>
                        <Table.HeadCell className="text-right">
                          Atrasadas
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {(activities.byOwner || []).map((row) => (
                          <Table.Row key={row.ownerId}>
                            <Table.Cell className="text-xs">
                              {row.ownerName || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-right text-xs">
                              {row.done}
                            </Table.Cell>
                            <Table.Cell className="text-right text-xs">
                              {row.pending}
                            </Table.Cell>
                            <Table.Cell className="text-right text-xs">
                              {row.overdue}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        {(activities.byOwner || []).length === 0 && (
                          <Table.Row>
                            <Table.Cell
                              colSpan={4}
                              className="py-3 text-center text-xs text-gray-400"
                            >
                              Sem atividades no período.
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Análises de tempo */}
          <Card className="border border-slate-200/80 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">
                Análises de tempo
              </h2>
              <Badge color="indigo" size="xs">
                Timeline
              </Badge>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-medium text-gray-500">
                  <span>Deals por semana</span>
                  <span>
                    {(timeAnalytics.dealsByWeek || []).reduce(
                      (sum, row) => sum + (row.created || 0),
                      0
                    )}{" "}
                    criados
                  </span>
                </div>
                <div className="h-32 w-full">
                  {(timeAnalytics.dealsByWeek || []).length === 0 ? (
                    <div className="flex h-full items-center justify-center text-[11px] text-gray-400">
                      Sem dados
                    </div>
                  ) : (
                    <Chart
                      options={dealsWeeklyOptions}
                      series={dealsWeeklySeries}
                      type="line"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] font-medium text-gray-500">
                  <span>Leads por semana</span>
                  <span>
                    {(timeAnalytics.leadsByWeek || []).reduce(
                      (sum, row) => sum + (row.total || 0),
                      0
                    )}{" "}
                    leads
                  </span>
                </div>
                <div className="h-32 w-full">
                  {(timeAnalytics.leadsByWeek || []).length === 0 ? (
                    <div className="flex h-full items-center justify-center text-[11px] text-gray-400">
                      Sem dados
                    </div>
                  ) : (
                    <Chart
                      options={leadsWeeklyOptions}
                      series={leadsWeeklySeries}
                      type="area"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-medium text-gray-500">
                  Deals por dia da semana
                </p>
                <div className="h-32 w-full">
                  {(timeAnalytics.dealsByWeekday || []).length === 0 ? (
                    <div className="flex h-full items-center justify-center text-[11px] text-gray-400">
                      Sem dados
                    </div>
                  ) : (
                    <Chart
                      options={weekdayOptions}
                      series={weekdaySeries}
                      type="bar"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-medium text-gray-500">
                  Deals por horário
                </p>
                <div className="h-32 w-full">
                  {(timeAnalytics.dealsByHour || []).length === 0 ? (
                    <div className="flex h-full items-center justify-center text-[11px] text-gray-400">
                      Sem dados
                    </div>
                  ) : (
                    <Chart
                      options={hourOptions}
                      series={hourSeries}
                      type="bar"
                      height="100%"
                      width="100%"
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportsOverviewPage;
